// Imports
import { PropsWithChildren, useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { categoryMapping } from '..';


// Styled components
const Wrapper = styled.button<{ $hasBubble: boolean }>`
	border: 0;
	margin: 0;
	padding: 0;
	height: 2.5rem;
	
	background: transparent;
	position: relative;
	
	cursor: pointer;
	
	transition: color 0.3s ease;
	-webkit-tap-highlight-color: transparent;
	
	font-size: 1.25rem;
	line-height: 1;
	
	color: hsl(206, 25%, 20%);
	
	&[data-active] {
		color: hsl(0, 0%, 100%);
	}
	
	${({ $hasBubble }) =>
		!$hasBubble &&
		css`
			&[data-active] {
				background: hsl(96, 54%, 59%);
				border-radius: 624.9375rem;
			}
		`}
`;

const Content = styled.span`
	z-index: 1;
	
	position: relative;
	display: flex;
	padding: 0.5rem 0.75rem;
	align-items: center;
	gap: 0.3125rem;
`;


// Define the accepted props
interface Props extends PropsWithChildren {
	identifier: keyof typeof categoryMapping;
	view: keyof typeof categoryMapping;
	setView: React.Dispatch<React.SetStateAction<keyof typeof categoryMapping>>;
	hasBubble: boolean;
}


// Function component
const Button: React.FC<Props> = ({ identifier, view, setView, hasBubble, children }) => {
	// Use ref
	const wrapperRef = useRef<HTMLButtonElement>(null);
	
	
	// Initialize
	const isActive = identifier === view;
	
	
	// Update attribute so it always matches correctly
	useLayoutEffect(() => {
		wrapperRef.current?.toggleAttribute('data-active', isActive);
	}, [isActive]);
	
	
	// Return JSX
	return (
		<Wrapper
			ref={wrapperRef}
			$hasBubble={hasBubble}
			data-identifier={identifier}
			{...(isActive && { 'data-active': true })}
			onClick={() => setView(identifier)}
		>
			<Content>{children}</Content>
		</Wrapper>
	);
};

export default Button;
